/**
 * Core HTTP client for the ServiceVault gateway.
 *
 * Responsibilities (docs/frontend/01-frontend-system.md, backend gateway contract):
 *  - Bearer injection from tokenStore
 *  - X-Correlation-Id per request (echoed back by the gateway; surfaced on errors)
 *  - NO tenant/user headers — the gateway derives and injects them from the JWT
 *  - Single-flight refresh: on 401 (or a stale token detected pre-flight) exactly
 *    one refresh call runs; concurrent requests queue behind it. Refresh tokens
 *    rotate, so a parallel second refresh would revoke the first — never race it.
 *  - RFC 7807 problem parsing → typed errors (see errors.ts)
 *  - Exponential backoff retry for network failures on idempotent requests
 */
import { API_BASE_URL, REQUEST_TIMEOUT_MS, ROUTES } from './config';
import { ApiError, AuthError, NetworkError, errorFromResponse } from './errors';
import { tokenStore, type TokenBundle } from './tokenStore';

export interface RequestOptions {
  /** Skip Authorization header (public endpoints: catalog, quotes, cms/public…). */
  anonymous?: boolean;
  /** Sent as Idempotency-Key — required by booking creation. */
  idempotencyKey?: string;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /**
   * Explicit bearer token, bypassing tokenStore. tokenStore lives in localStorage,
   * which doesn't exist in a Next.js server action / route handler — callers running
   * there (e.g. cleaning-app's server actions, which read the token from a cookie via
   * next/headers) pass their own token through here instead.
   */
  accessToken?: string;
  /**
   * Fires with the raw response Headers right after the response arrives (before body
   * parsing). For endpoints that hand back state via a header, not the body — e.g.
   * CartService mints a guest session id into X-Guest-Session-Id on a caller's first
   * request, never in the JSON payload.
   */
  onHeaders?: (headers: Headers) => void;
}

type AuthFailureListener = () => void;
const authFailureListeners = new Set<AuthFailureListener>();
/** Apps register a handler to e.g. redirect to /signin when the session dies. */
export function onAuthFailure(listener: AuthFailureListener): () => void {
  authFailureListeners.add(listener);
  return () => authFailureListeners.delete(listener);
}

function correlationId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID().replace(/-/g, '');
  }
  return Math.random().toString(16).slice(2) + Date.now().toString(16);
}

// ── single-flight refresh ──
let refreshInFlight: Promise<TokenBundle | null> | null = null;

/** Exported alongside ensureFreshToken for raw-fetch callers' reactive 401-retry path. */
export async function refreshTokens(): Promise<TokenBundle | null> {
  if (refreshInFlight) return refreshInFlight;
  const current = tokenStore.get();
  if (!current?.refreshToken) return null;

  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}${ROUTES.auth.refresh}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: current.refreshToken }),
      });
      if (!response.ok) {
        // Refresh token expired/revoked → the session is over.
        tokenStore.clear();
        authFailureListeners.forEach((l) => l());
        return null;
      }
      const pair = (await response.json()) as {
        accessToken: string;
        accessExpiresAt: string;
        refreshToken: string;
      };
      tokenStore.set(pair);
      return pair;
    } catch {
      return null; // network blip — leave tokens; a later request retries
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/** Exported for raw-fetch multipart callers (productMedia.ts, images.ts) that can't go
 * through execute() — FormData bodies aren't JSON, but they still need the same proactive
 * "refresh if stale before sending" as every other authenticated request. */
export async function ensureFreshToken(): Promise<string | null> {
  if (tokenStore.isAccessTokenFresh()) return tokenStore.get()!.accessToken;
  const refreshed = await refreshTokens();
  return refreshed?.accessToken ?? tokenStore.get()?.accessToken ?? null;
}

const RETRYABLE_METHODS = new Set(['GET', 'HEAD']);
const MAX_NETWORK_RETRIES = 2;

async function execute<T>(method: string, path: string, body: unknown, options: RequestOptions, attempt = 0): Promise<T> {
  const headers: Record<string, string> = {
    'X-Correlation-Id': correlationId(),
    ...options.headers,
  };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;

  if (!options.anonymous) {
    const token = options.accessToken ?? (await ensureFreshToken());
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  options.signal?.addEventListener('abort', () => controller.abort(), { once: true });

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } catch (cause) {
    if (RETRYABLE_METHODS.has(method) && attempt < MAX_NETWORK_RETRIES) {
      await new Promise((r) => setTimeout(r, 300 * 2 ** attempt));
      return execute<T>(method, path, body, options, attempt + 1);
    }
    throw new NetworkError(cause);
  } finally {
    clearTimeout(timeout);
  }

  options.onHeaders?.(response.headers);

  // 401 with a valid-looking token → the access token just expired server-side;
  // refresh once and replay. (Anonymous calls never get here.)
  if (response.status === 401 && !options.anonymous && attempt === 0) {
    const refreshed = await refreshTokens();
    if (refreshed) return execute<T>(method, path, body, options, attempt + 1);
  }

  const corr = response.headers.get('X-Correlation-Id');

  if (!response.ok) {
    let problem: unknown = null;
    try {
      problem = await response.json();
    } catch {
      /* non-JSON error body */
    }
    const error = errorFromResponse(response.status, problem, corr);
    // Anonymous calls carry no Authorization header, so a 401 from one says nothing about
    // whether the current session is still valid — it means the "anonymous" endpoint itself
    // is misconfigured server-side. Treating it as a session failure force-logs-out the whole
    // app over an unrelated public call (e.g. blog categories) returning 401.
    if (error instanceof AuthError && error.status === 401 && !options.anonymous) {
      tokenStore.clear();
      authFailureListeners.forEach((l) => l());
    }
    throw error;
  }

  if (response.status === 204) return undefined as T;
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('json')) return (await response.json()) as T;
  return (await response.blob()) as T; // CSV export etc.
}

export const http = {
  get: <T>(path: string, options: RequestOptions = {}) => execute<T>('GET', path, undefined, options),
  post: <T>(path: string, body?: unknown, options: RequestOptions = {}) => execute<T>('POST', path, body, options),
  put: <T>(path: string, body?: unknown, options: RequestOptions = {}) => execute<T>('PUT', path, body, options),
  patch: <T>(path: string, body?: unknown, options: RequestOptions = {}) => execute<T>('PATCH', path, body, options),
  delete: <T>(path: string, options: RequestOptions = {}) => execute<T>('DELETE', path, undefined, options),
};

export { ApiError };
